<template>
  <div class="data-table">
    <DataTable
      :value="data"
      :paginator="true"
      :rows="10"
      :rowsPerPageOptions="[5, 10, 20, 50]"
      :loading="loading"
      :filters="filters"
      filterDisplay="menu"
      :globalFilterFields="globalFilterFields"
      responsiveLayout="scroll"
      class="p-datatable-sm"
      v-model:filters="filters"
      v-model:selection="selectedItems"
      :selection="selection"
      dataKey="id"
    >
      <template #header>
        <div class="table-header">
          <h2 class="table-title">{{ title }}</h2>
          <div class="table-actions">
            <span class="p-input-icon-left">
              <InputText v-model="filters['global'].value" placeholder="Rechercher..." />
            </span>
            <slot name="header-actions"></slot>
          </div>
        </div>
      </template>

      <Column v-if="selection" selectionMode="multiple" headerStyle="width: 3rem"></Column>
      
      <template v-for="column in columns" :key="column.field">
        <Column
          :field="column.field"
          :header="column.header"
          :sortable="column.sortable"
          :filter="column.filter"
          :filterField="column.field"
          :showFilterMenu="column.filter"
          :filterMatchMode="column.filterMatchMode || 'contains'"
          :style="column.style"
        >
          <template #body="slotProps" v-if="column.template">
            <slot :name="column.field" :row="slotProps.data"></slot>
          </template>
        </Column>
      </template>

      <template #empty>
        <div class="p-d-flex p-jc-center p-ai-center p-p-4">
          <p>Aucune donnée disponible</p>
        </div>
      </template>

      <template #loading>
        <div class="p-d-flex p-jc-center p-ai-center p-p-4">
          <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
        </div>
      </template>
    </DataTable>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import { FilterMatchMode } from 'primevue/api'

export default {
  name: 'DataTableComponent',
  components: {
    DataTable,
    Column,
    InputText
  },
  props: {
    title: {
      type: String,
      required: true
    },
    data: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    selection: {
      type: Boolean,
      default: false
    },
    globalFilterFields: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:selection'],
  setup(props, { emit }) {
    const filters = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    })
    const selectedItems = ref([])

    onMounted(() => {
      // Initialiser les filtres pour chaque colonne
      props.columns.forEach(column => {
        if (column.filter) {
          filters.value[column.field] = {
            value: null,
            matchMode: column.filterMatchMode || FilterMatchMode.CONTAINS
          }
        }
      })
    })

    return {
      filters,
      selectedItems
    }
  }
}
</script>

<style lang="scss" scoped>
.data-table {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.table-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.table-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

:deep(.p-datatable) {
  .p-datatable-header {
    padding: 0;
    background: transparent;
    border: none;
  }

  .p-datatable-thead > tr > th {
    background: #f8f9fa;
    color: var(--text-color);
    font-weight: 600;
    padding: 0.75rem 1rem;
  }

  .p-datatable-tbody > tr > td {
    padding: 0.75rem 1rem;
  }

  .p-datatable-tbody > tr:hover {
    background: #f8f9fa;
  }
}
</style> 